
// import { CONFERENCE_JOINED } from '../base/conference';
import { SET_SCREENSHARE_MUTED } from '../base/media/actionTypes';
import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media/constants';
import {
    DOMINANT_SPEAKER_CHANGED,
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    PIN_PARTICIPANT,
    SET_MODERATOR_PIN,
    getLocalParticipant
} from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { isTestModeEnabled } from '../base/testing';
import {
    TRACK_ADDED,
    TRACK_REMOVED,
    TRACK_UPDATED
} from '../base/tracks';
import { TOGGLE_DOCUMENT_EDITING } from '../etherpad/actionTypes';

import { selectParticipantInLargeVideo, setLargeVideoLastDominantSpeaker } from './actions';
import logger from './logger';

import './subscriber';

const AUTO_SELECTION_DELAY_MS = 120;
let _autoSelectionTimer = null;

/**
 * Clears the pending auto-selection timer.
 *
 * @returns {void}
 */
function _clearAutoSelectionTimer() {
    if (_autoSelectionTimer) {
        clearTimeout(_autoSelectionTimer);
        _autoSelectionTimer = null;
    }
}

/**
 * Schedules a coalesced automatic large-video selection update.
 *
 * @param {Store} store - The redux store.
 * @param {string} selectionSource - Source metadata for the selection action.
 * @returns {void}
 */
function _scheduleAutoSelection(store, selectionSource) {
    _clearAutoSelectionTimer();
    _autoSelectionTimer = setTimeout(() => {
        _autoSelectionTimer = null;
        store.dispatch(selectParticipantInLargeVideo(undefined, {
            selectionSource
        }));
    }, AUTO_SELECTION_DELAY_MS);
}

/**
 * Dispatches an immediate, non-debounced large-video selection update.
 *
 * @param {Store} store - The redux store.
 * @param {string} selectionSource - Source metadata for the selection action.
 * @returns {void}
 */
function _dispatchPrioritySelection(store, selectionSource) {
    _clearAutoSelectionTimer();
    store.dispatch(selectParticipantInLargeVideo(undefined, {
        selectionSource,
        forceSelection: true
    }));
}

/**
 * Middleware that catches actions related to participants and tracks and
 * dispatches an action to select a participant depicted by LargeVideo.
 *
 * @param {Store} store - Redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);

    switch (action.type) {
    case DOMINANT_SPEAKER_CHANGED: {
        const state = store.getState();
        const localParticipant = getLocalParticipant(state);
        const pinnedParticipant = state['features/base/participants'].find(p => p.pinned);

        if (isTestModeEnabled(state)) {
            //  logger.info(`Dominant speaker changed event for: ${action.participant.id}`);
        }

        if (localParticipant && localParticipant.id !== action.participant.id) {
            // Remember the last remote dominant speaker so the large video can stay stable on
            // them (tier 4 of the election hierarchy) once they stop talking/mute, instead of
            // jumping to a random participant.
            store.dispatch(setLargeVideoLastDominantSpeaker(action.participant.id));

            if (!pinnedParticipant) {
                _scheduleAutoSelection(store, 'dominant-speaker');
            }
        }

        break;
    }
    case PARTICIPANT_JOINED:
    case PARTICIPANT_LEFT:
    case TOGGLE_DOCUMENT_EDITING:
        _scheduleAutoSelection(store, 'participant-or-track-change');
        break;

    case TRACK_ADDED:
    case TRACK_REMOVED:
        // A screenshare (desktop) source being added/removed must immediately re-run
        // large video selection on ALL platforms (web and native). Otherwise the
        // anti-flicker throttling can drop the switch to the new screenshare source,
        // leaving the bridge's receiver constraints pinned to the previous (stopped)
        // source. That is what caused the 2nd and subsequent screenshares to stall
        // with no frames on web while the first one worked.
        if (action.track
                && (action.track.videoType === VIDEO_TYPE.DESKTOP
                    || action.track.mediaType === MEDIA_TYPE.SCREENSHARE)) {
            _dispatchPrioritySelection(store, 'screenshare-track-change');
        } else {
            _scheduleAutoSelection(store, 'participant-or-track-change');
        }
        break;

    // `next(action)` above already applied the pin/unpin to `features/base/participants` state (the
    // reducer also clears `pinned` on every other participant, so exclusivity is guaranteed) before this
    // runs. `_dispatchPrioritySelection` re-elects from that up-to-date state, and its tier-1 check is
    // exactly "is anyone pinned" - so a newly-pinned participant is already the election's top choice
    // with no need to thread the id through separately.
    case PIN_PARTICIPANT:
    case SET_MODERATOR_PIN:
        _dispatchPrioritySelection(store, 'pin-change');
        break;

    case SET_SCREENSHARE_MUTED:
        // Force on both web and native so starting/stopping a screenshare always
        // re-elects the stage participant and refreshes receiver constraints.
        _dispatchPrioritySelection(store, 'screenshare-muted-change');
        break;

    case TRACK_UPDATED: {
        if (action.track?.mhTrack) {
            // Re-select when a desktop (screenshare) track's muted state changes.
            if ('muted' in action.track) {
                const { videoType, mediaType } = action.track;

                if (videoType === VIDEO_TYPE.DESKTOP || mediaType === MEDIA_TYPE.SCREENSHARE) {
                    _dispatchPrioritySelection(store, 'screenshare-track-change');
                } else if (mediaType === MEDIA_TYPE.VIDEO) {
                    _scheduleAutoSelection(store, 'video-muted-change');
                }
            }

            // Re-select when a track's videoType changes (e.g. camera -> desktop),
            // so the large video pins the new screenshare participant.
            if ('videoType' in action.track) {
                _dispatchPrioritySelection(store, 'track-videotype-change');
            }
        }
        break;
    }
    }

    return result;
});

/**
 * State listener to handle participant state consistency for large video during room switching
 */
StateListenerRegistry.register(
    state => {
        return {
            participants: state['features/base/participants'],
            mhConferenceJoined: state['features/base/conference']?.mhConferenceJoined,
            largeVideoParticipantId: state['features/large-video']?.participantId,
            isSwitchingRoom: state['features/base/mtApi']?.isSwitchingBreakOutRoom
        };
    },
    ({ participants, mhConferenceJoined, largeVideoParticipantId, isSwitchingRoom }, { dispatch }) => {
        // During room switching, ensure large video participant is valid
        if (isSwitchingRoom && largeVideoParticipantId && participants) {
            const participant = participants.find(p => p?.id === largeVideoParticipantId);

            if (!participant) {
                logger.debug('Large video participant not found during room switch, selecting new participant');
                dispatch(selectParticipantInLargeVideo(undefined, {
                    selectionSource: 'stale-participant-correction',
                    forceSelection: true
                }));
            }
        }

        // If participants list is empty but we're not switching rooms, something went wrong
        if (mhConferenceJoined && !isSwitchingRoom && (!participants || participants.length === 0)) {
            logger.warn('Participants list is empty but not switching rooms');
        }
    },

    // The selector builds a new value every time; compare by content so the listener runs only on a change
    // instead of on every redux action.
    {
        deepEquals: true
    }
);
